import type { ReactNode } from 'react'
import { Info } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Tooltip, TooltipTrigger, TooltipContent, TooltipProvider,
} from '../ui/tooltip'

// Two little ⓘ info badges — one for images, one for video — that surface the
// recommended aspect ratios + pixel sizes for each orientation (horizontal /
// vertical / square) on hover or focus. Dropped into the asset-upload modals so
// operators know what to prepare before they crop/upload. Pure presentational;
// no props needed beyond optional layout className.

type SpecRow = { label: string; dims: string; ratio: string }

function SpecPanel({ title, rows, note }: { title: string; rows: SpecRow[]; note?: string }) {
  return (
    <div className="space-y-1.5">
      <p className="font-semibold text-[11px] uppercase tracking-wide">{title}</p>
      <ul className="space-y-1">
        {rows.map(r => (
          <li key={r.label} className="flex items-center justify-between gap-4 text-xs">
            <span className="opacity-80">{r.label}</span>
            <span className="font-mono whitespace-nowrap">{r.dims} · {r.ratio}</span>
          </li>
        ))}
      </ul>
      {note && <p className="text-[11px] opacity-70 pt-0.5">{note}</p>}
    </div>
  )
}

function SpecBadge({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <Info className="w-4 h-4" />
          <span className="text-xs font-medium">{label}</span>
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        align="end"
        sideOffset={6}
        className="bg-popover text-popover-foreground border border-border shadow-md p-3 max-w-[260px]"
      >
        {children}
      </TooltipContent>
    </Tooltip>
  )
}

export function AssetSpecsHint({ className }: { className?: string }) {
  const { t } = useTranslation()
  const horizontal = t('assetSpecs.horizontal', 'Horizontal')
  const vertical   = t('assetSpecs.vertical', 'Vertical')
  const square     = t('assetSpecs.square', 'Square')

  const imageRows: SpecRow[] = [
    { label: horizontal, dims: '1920 × 1080', ratio: '16:9' },
    { label: vertical,   dims: '1080 × 1920', ratio: '9:16' },
    { label: square,     dims: '1080 × 1080', ratio: '1:1' },
  ]
  const videoRows: SpecRow[] = [
    { label: horizontal, dims: '1920 × 1080', ratio: '16:9' },
    { label: vertical,   dims: '1080 × 1920', ratio: '9:16' },
  ]

  return (
    <TooltipProvider delayDuration={150}>
      <div className={`flex items-center gap-4 ${className ?? ''}`}>
        <SpecBadge label={t('assetSpecs.image', 'Image')}>
          <SpecPanel title={t('assetSpecs.imageTitle', 'Recommended image specs')} rows={imageRows} />
        </SpecBadge>
        <SpecBadge label={t('assetSpecs.video', 'Video')}>
          <SpecPanel
            title={t('assetSpecs.videoTitle', 'Recommended video specs')}
            rows={videoRows}
            note={t('assetSpecs.videoNote', 'MP4 (H.264) · up to 30 fps')}
          />
        </SpecBadge>
      </div>
    </TooltipProvider>
  )
}

export default AssetSpecsHint
